import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, User, PiggyBank, ArrowUpCircle, ArrowDownCircle, ArrowLeftRight, Download, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api, { savingsAccountsApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const SavingsLookupPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [clientResult, setClientResult] = useState(null);
  const [multipleResults, setMultipleResults] = useState([]);
  const [showTransaction, setShowTransaction] = useState(null);
  const [txAccountId, setTxAccountId] = useState('');
  const [txAmount, setTxAmount] = useState('');
  const [txLoading, setTxLoading] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferData, setTransferData] = useState({ to_account_id: '', amount: '', description: '' });
  const [transferLoading, setTransferLoading] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const inputRef = useRef(null);
  const [showNewAccountModal, setShowNewAccountModal] = useState(false);
  const [savingsProducts, setSavingsProducts] = useState([]);
  const [savingsClients, setSavingsClients] = useState([]);
  const [accountForm, setAccountForm] = useState({ client_id: '', product_id: '' });
  const [accountFormLoading, setAccountFormLoading] = useState(false);

  useEffect(() => {
    if (showNewAccountModal) {
      api.get('/savings-products').then(r => setSavingsProducts((r.data || []).filter(p => p.is_active))).catch(() => {});
      api.get('/clients?status=active&limit=100').then(r => setSavingsClients(r.data.clients || [])).catch(() => {});
    }
  }, [showNewAccountModal]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setClientResult(null);
    setMultipleResults([]);
    try {
      const res = await api.get(`/clients/lookup?q=${encodeURIComponent(query.trim())}`);
      if (res.data.match === 'single') {
        setClientResult(res.data);
      } else if (res.data.match === 'multiple') {
        setMultipleResults(res.data.clients);
      } else {
        toast.error('No client found. Try account number, ID number, or name.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed');
    }
    setSearching(false);
  };

  const selectClient = async (clientId) => {
    setSearching(true);
    try {
      const res = await api.get(`/clients/lookup?q=${clientId}`);
      if (res.data.match === 'single') {
        setClientResult(res.data);
        setMultipleResults([]);
      }
    } catch { toast.error('Failed to load client'); }
    setSearching(false);
  };

  const handleTransaction = async (type) => {
    if (!txAccountId || !txAmount) return;
    setTxLoading(true);
    try {
      if (type === 'deposit') {
        await savingsAccountsApi.deposit(txAccountId, { amount: parseFloat(txAmount) });
      } else {
        await savingsAccountsApi.withdraw(txAccountId, { amount: parseFloat(txAmount) });
      }
      toast.success(`${type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${formatCurrency(parseFloat(txAmount))} successful`);
      setShowTransaction(null);
      setTxAmount('');
      handleSearch();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${type} failed`);
    }
    setTxLoading(false);
  };

  const openTransfer = async (fromAccountId) => {
    setTxAccountId(fromAccountId);
    setShowTransfer(true);
    try {
      const res = await savingsAccountsApi.list({ limit: 100 });
      setAllAccounts(res.data.accounts.filter(a => a.id !== fromAccountId && a.status === 'active'));
    } catch {}
  };

  const handleTransfer = async () => {
    setTransferLoading(true);
    try {
      const res = await api.post('/transfers/internal', {
        from_account_id: txAccountId,
        to_account_id: transferData.to_account_id,
        amount: parseFloat(transferData.amount),
        description: transferData.description || 'Internal transfer',
      });
      toast.success(`Transfer successful. Ref: ${res.data.reference}`);
      setShowTransfer(false);
      setTransferData({ to_account_id: '', amount: '', description: '' });
      handleSearch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transfer failed');
    }
    setTransferLoading(false);
  };

  const handleDownloadStatement = async (accountId, accountNumber) => {
    try {
      const res = await api.get(`/savings-accounts/${accountId}/statement`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Statement_${accountNumber}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch { toast.error('Failed to download statement'); }
  };

  const c = clientResult?.client;
  const accounts = clientResult?.accounts || [];

  return (
    <div className="space-y-6 fade-in" data-testid="savings-lookup-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Dashboard</span><span>/</span><span className="text-[#1f1f1f] font-medium">Savings Account Lookup</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">DEPOSIT MANAGEMENT</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Savings Account Lookup</h1>
          <p className="text-sm text-gray-500 mt-1">Search by account number, client name, or ID number</p>
        </div>
        <Button
          onClick={() => setShowNewAccountModal(true)}
          className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
          data-testid="new-savings-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New Account
        </Button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-3" data-testid="savings-search-form">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter account number, client name, or ID number..."
            className="pl-12 py-6 text-base border-gray-200 focus:border-[#1f1f1f] focus:ring-[#1f1f1f]"
            data-testid="savings-lookup-search-input"
          />
        </div>
        <Button type="submit" disabled={searching} className="bg-[#1f1f1f] hover:bg-[#333333] px-8 py-6" data-testid="savings-lookup-search-btn">
          {searching ? 'Searching...' : 'Search'}
        </Button>
      </form>

      {/* Multiple Results */}
      {multipleResults.length > 0 && (
        <div className="bg-white border border-gray-200 p-4" data-testid="savings-multiple-results">
          <p className="text-sm font-medium text-[#1f1f1f] mb-3">Multiple clients found — select one:</p>
          <div className="space-y-2">
            {multipleResults.map(mc => (
              <button
                key={mc.id}
                onClick={() => selectClient(mc.client_number || mc.id_number)}
                className="w-full flex items-center gap-4 p-3 border border-gray-200 hover:bg-gray-50 text-left transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#1f1f1f]/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#1f1f1f]/50" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1f1f1f]">{mc.first_name} {mc.last_name}</p>
                  <p className="text-xs text-gray-500">{mc.client_number} | ID: {mc.id_number}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Client with Accounts */}
      {c && (
        <div className="space-y-4" data-testid="savings-client-result">
          <div className="bg-[#1f1f1f] text-white p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <User className="w-5 h-5 text-white/60" />
            </div>
            <div>
              <h2 className="text-base font-bold">{c.first_name} {c.last_name}</h2>
              <p className="text-xs text-gray-400">{c.client_number} | {c.id_number} | {c.phone}</p>
            </div>
          </div>

          {accounts.length === 0 ? (
            <div className="text-center py-8 bg-white border border-gray-200">
              <PiggyBank className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">No savings accounts for this client</p>
              <Button size="sm" variant="outline" onClick={() => { setAccountForm(p => ({...p, client_id: c.id})); setShowNewAccountModal(true); }} data-testid="create-account-for-client-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Open New Account
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {accounts.map(acc => (
                <div key={acc.id} className="bg-white border border-gray-200 p-4" data-testid={`savings-account-card-${acc.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#1f1f1f]">{acc.account_number}</p>
                      <p className="text-xs text-gray-500">{acc.product_name || 'Savings'} | {acc.status}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#1f1f1f]">{formatCurrency(acc.balance)}</p>
                      <p className="text-xs text-gray-400">Available: {formatCurrency(acc.available_balance)}</p>
                    </div>
                  </div>
                  {acc.status === 'active' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => { setTxAccountId(acc.id); setShowTransaction('deposit'); }} data-testid={`savings-deposit-${acc.id}`}>
                        <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />Deposit
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => { setTxAccountId(acc.id); setShowTransaction('withdraw'); }} data-testid={`savings-withdraw-${acc.id}`}>
                        <ArrowDownCircle className="w-3.5 h-3.5 mr-1" />Withdraw
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => openTransfer(acc.id)} data-testid={`savings-transfer-${acc.id}`}>
                        <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Transfer
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs" onClick={() => handleDownloadStatement(acc.id, acc.account_number)}>
                        <Download className="w-3.5 h-3.5 mr-1" />Statement
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!clientResult && multipleResults.length === 0 && !searching && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Search for a client to view their savings accounts</p>
        </div>
      )}

      {/* Deposit/Withdraw Dialog */}
      <Dialog open={!!showTransaction} onOpenChange={() => setShowTransaction(null)}>
        <DialogContent className="max-w-md" aria-describedby="tx-desc">
          <DialogHeader><DialogTitle>{showTransaction === 'deposit' ? 'Make Deposit' : 'Make Withdrawal'}</DialogTitle></DialogHeader>
          <p id="tx-desc" className="sr-only">Transaction form</p>
          <div className="space-y-4">
            <div><Label>Amount (ZAR)</Label><Input type="number" value={txAmount} onChange={(e) => setTxAmount(e.target.value)} placeholder="0.00" data-testid="savings-tx-amount" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransaction(null)}>Cancel</Button>
            <Button onClick={() => handleTransaction(showTransaction)} disabled={txLoading}
              className={showTransaction === 'deposit' ? 'bg-emerald-600' : 'bg-red-600'} data-testid="savings-confirm-tx">
              {txLoading ? 'Processing...' : showTransaction === 'deposit' ? 'Deposit' : 'Withdraw'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransfer} onOpenChange={() => setShowTransfer(false)}>
        <DialogContent className="max-w-md" aria-describedby="tf-desc">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[#C5A059]" />Internal Transfer</DialogTitle></DialogHeader>
          <p id="tf-desc" className="sr-only">Transfer form</p>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">To Account</Label>
              <Select value={transferData.to_account_id} onValueChange={(v) => setTransferData(p => ({ ...p, to_account_id: v }))}>
                <SelectTrigger data-testid="savings-transfer-to"><SelectValue placeholder="Select destination account" /></SelectTrigger>
                <SelectContent>{allAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.account_number} - {a.client_name} ({formatCurrency(a.balance)})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Amount (ZAR)</Label><Input type="number" value={transferData.amount} onChange={(e) => setTransferData(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label><Input value={transferData.description} onChange={(e) => setTransferData(p => ({ ...p, description: e.target.value }))} placeholder="Reference" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={transferLoading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="savings-confirm-transfer">
              {transferLoading ? 'Processing...' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Savings Account Modal */}
      <Dialog open={showNewAccountModal} onOpenChange={setShowNewAccountModal}>
        <DialogContent className="max-w-lg" aria-describedby="new-account-desc">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Open New Savings Account</DialogTitle>
          </DialogHeader>
          <p id="new-account-desc" className="sr-only">Open a new savings account for a client</p>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setAccountFormLoading(true);
            try {
              await savingsAccountsApi.create(accountForm);
              toast.success('Savings account opened successfully');
              setShowNewAccountModal(false);
              setAccountForm({ client_id: '', product_id: '' });
            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to open account'); }
            setAccountFormLoading(false);
          }} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Client *</Label>
              <Select value={accountForm.client_id} onValueChange={v => setAccountForm(p => ({...p, client_id: v}))}>
                <SelectTrigger data-testid="lookup-savings-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{savingsClients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Savings Product *</Label>
              <Select value={accountForm.product_id} onValueChange={v => setAccountForm(p => ({...p, product_id: v}))}>
                <SelectTrigger data-testid="lookup-savings-product"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{savingsProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.interest_rate}% p.a.)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewAccountModal(false)}>Cancel</Button>
              <Button type="submit" disabled={accountFormLoading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-new-account-btn">{accountFormLoading ? 'Opening...' : 'Open Account'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavingsLookupPage;
